// src/components/ProjectsPreviewCarousel.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";

/* ------------------------------- CONFIG ------------------------------- */
const GAP = 14;                              // space between cards
const SPRING = { stiffness: 260, damping: 34, mass: 0.9 };

const CARD_MIN = 240;                        // clamp to keep size sane
const CARD_MAX = 400;
const CARD_DEFAULT = 310;                    // safe first render width
const CARD_HEIGHT = 320;                     // consistent height (increased for better spacing)

const SUMMARY_CHARS = 200;

/* -------------------------------- UTILS ------------------------------- */
const summarize = (t = "", n = SUMMARY_CHARS) =>
  t && t.length > n ? t.slice(0, n - 1) + "…" : t || "";

const byDateDesc = (a, b) =>
  new Date(b.completion_date || b.end_date || 0) -
  new Date(a.completion_date || a.end_date || 0);

function fmtRange(p) {
  const fmt = (d) =>
    d && !Number.isNaN(new Date(d).getTime())
      ? new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(new Date(d))
      : null;
  const s = fmt(p.start_date);
  const e = fmt(p.end_date);
  const c = fmt(p.completion_date);
  if (s && e) return `${s} – ${e}`;
  if (s && !e) return `${s} – Present`;
  if (c) return c;
  return "";
}

/* -------------------------- PRESENTATION CARD ------------------------- */
function ProjectCard({ p }) {
  const [showMedia, setShowMedia] = useState(false);
  const hasMedia = p.media && p.media.url;

  return (
    <article
      className="group relative w-full border-l-4 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 transition-all duration-300
                 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg cursor-pointer overflow-hidden"
      style={{ height: CARD_HEIGHT }}
      onMouseEnter={() => hasMedia && setShowMedia(true)}
      onMouseLeave={() => setShowMedia(false)}
    >
      {/* Media Preview Overlay */}
      <AnimatePresence>
        {hasMedia && showMedia && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-10 bg-slate-900 dark:bg-slate-950 flex items-center justify-center p-3"
          >
            {p.media.type === 'video' ? (
              <video
                src={p.media.url}
                autoPlay
                loop
                muted
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <motion.img
                src={p.media.url}
                alt={p.title}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
                className="max-w-full max-h-full object-contain"
              />
            )}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="absolute bottom-3 right-3 px-2 py-1 bg-yellow-400 text-slate-900 text-xs font-mono font-bold border-2 border-slate-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
            >
              PREVIEW
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex h-full flex-col relative z-0">
        {/* Title & Featured */}
        <div className="mb-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-tight line-clamp-2 flex-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {p.title}
            </h3>
            {hasMedia && (
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="shrink-0 relative"
              >
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border border-white animate-pulse"></span>
              </motion.div>
            )}
          </div>
          {p.featured && (
            <motion.span
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              whileHover={{ scale: 1.05 }}
              className="inline-block mt-1 text-[11px] font-mono font-black px-2 py-1 bg-yellow-400 text-slate-900 uppercase tracking-wider border-2 border-slate-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-shadow"
            >
              ★ FEATURED
            </motion.span>
          )}
        </div>

        {/* Date */}
        {fmtRange(p) && (
          <div className="text-xs font-mono text-slate-500 mb-3 group-hover:text-blue-600 transition-colors">
            {fmtRange(p)}
          </div>
        )}

        {/* Category Tags */}
        {p.tags && p.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {p.tags.slice(0, 3).map((tag, idx) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.05 }}
                className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 uppercase leading-none hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {tag}
              </motion.span>
            ))}
          </div>
        )}

        {/* Description */}
        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3 mb-3 leading-relaxed group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors flex-1">
          {summarize(p.description, 140)}
        </p>

        {/* Impact Indicator */}
        {p.impact && (
          <div className="mb-2">
            <span className="text-[9px] font-bold px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 uppercase leading-none">
              ✓ Impact Metrics
            </span>
          </div>
        )}

        {/* Tech stack */}
        <div className="mt-auto pt-2 border-t border-slate-200 dark:border-slate-700 group-hover:border-blue-200 dark:group-hover:border-blue-600 transition-colors">
          <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400 mb-2 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors">
            {(p.technologies || []).slice(0, 4).join(' · ')}
            {(p.technologies || []).length > 4 && ` +${(p.technologies || []).length - 4}`}
          </div>

          {/* Links */}
          <div className="flex gap-3 text-sm font-medium">
            {p.live_demo && (
              <a
                href={p.live_demo}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Visit →
              </a>
            )}
            {p.github && (
              <a
                href={p.github}
                target="_blank"
                rel="noreferrer"
                className="text-slate-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Code
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div
      className="w-full border-l-4 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 animate-pulse transition-colors duration-300"
      style={{ height: CARD_HEIGHT }}
    >
      <div className="h-4 bg-slate-200 dark:bg-slate-700 w-3/4 mb-3"></div>
      <div className="h-3 bg-slate-100 dark:bg-slate-600 w-1/4 mb-4"></div>
      <div className="space-y-2 mb-4">
        <div className="h-3 bg-slate-100 dark:bg-slate-600 w-full"></div>
        <div className="h-3 bg-slate-100 dark:bg-slate-600 w-5/6"></div>
      </div>
      <div className="h-3 bg-slate-100 dark:bg-slate-600 w-2/3 mb-4"></div>
      <div className="flex gap-3">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 w-12"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 w-12"></div>
      </div>
    </div>
  );
}

/* ---------------------------- MAIN CAROUSEL --------------------------- */
export default function ProjectsPreviewCarousel({ data = [], maxPool = 10 }) {
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(false);

  // If no data passed, fetch from /public/data/projects.json
  useEffect(() => {
    if (data && data.length) return;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch("/data/projects.json");
        const d = await r.json();
        setFetched(d);
      } catch (e) {
        console.error("projects.json load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [data]);

  // Sort + take 4 (featured first)
  const base = useMemo(() => {
    const src = (data?.length ? data : fetched).slice(0, maxPool).sort(byDateDesc);
    const featured = src.filter((p) => p.featured);
    const rest = src.filter((p) => !p.featured);
    return [...featured, ...rest].slice(0, 4);
  }, [data, fetched, maxPool]);

  // Responsive columns + slide width
  const viewportRef = useRef(null);
  const [cols, setCols] = useState(3);
  const [cardW, setCardW] = useState(CARD_DEFAULT);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const compute = () => {
      const w = el.clientWidth || 0;
      // 1 / 2 / 3 columns
      const c = w < 640 ? 1 : w < 1024 ? 2 : 3;
      setCols(c);
      const raw = (w - GAP * (c - 1)) / (c || 1);
      const cw = Math.max(CARD_MIN, Math.min(CARD_MAX, Math.round(raw || CARD_DEFAULT)));
      setCardW(cw);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Build infinite track with clones
  const K = cols;
  const extended = useMemo(() => {
    if (!base.length) return [];
    return [...base.slice(-K), ...base, ...base.slice(0, K)];
  }, [base, K]);

  // Index + spring motion
  const [index, setIndex] = useState(K);
  const x = useMotionValue(0);
  const springX = useSpring(x, SPRING);

  useEffect(() => {
    const step = cardW + GAP;
    springX.set(-index * step);
  }, [index, cardW, springX]);

  // Normalize when hitting clones
  useEffect(() => {
    const n = base.length;
    if (!n) return;
    const first = K;
    const last = K + n - 1;
    const t = setTimeout(() => {
      setIndex((i) => {
        if (i < first) return i + n;
        if (i > last) return i - n;
        return i;
      });
    }, 330);
    return () => clearTimeout(t);
  }, [index, base.length, K]);

  const next = () => setIndex((i) => i + 1);
  const prev = () => setIndex((i) => i - 1);

  // Drag/swipe
  const dragProps = {
    drag: "x",
    dragConstraints: { left: 0, right: 0 },
    dragElastic: 0.2,
    onDragEnd: (_, info) => {
      const step = cardW + GAP;
      const moved = info.offset.x;
      const velocity = info.velocity.x;
      if (moved < -step / 3 || velocity < -300) next();
      else if (moved > step / 3 || velocity > 300) prev();
    },
  };

  /* ------------------------------ RENDER ------------------------------ */
  if (loading && base.length === 0) {
    return (
      <section className="mx-auto max-w-7xl px-6">
        <Header />
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </section>
    );
  }
  if (!base.length) return null;

  // If not enough to scroll, render a static grid (still compact)
  if (base.length <= cols) {
    return (
      <section className="mx-auto max-w-7xl px-6">
        <Header />
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {base.map((p) => <ProjectCard key={p.title} p={p} />)}
        </div>
        <Nudge />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-6">
      <Header />

      {/* viewport */}
      <div className="relative mt-5 overflow-visible">
        {/* edge fades (so content peeks) */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-6 z-10 bg-gradient-to-r from-white/90 to-transparent rounded-l-2xl" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-6 z-10 bg-gradient-to-l from-white/90 to-transparent rounded-r-2xl" />

        <div
          ref={viewportRef}
          className="-my-2 py-2 overflow-x-hidden overflow-y-visible"
        >
          <motion.div className="flex" style={{ x: springX, gap: GAP }} {...dragProps}>
            {extended.map((p, idx) => (
              <div
                key={`${p.title}-${idx}`}
                style={{ minWidth: cardW, maxWidth: cardW }}
              >
                <ProjectCard p={p} />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Navigation */}
        <motion.button
          aria-label="Previous"
          onClick={prev}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="pointer-events-auto absolute -left-4 top-1/2 -translate-y-1/2 z-20
                     w-10 h-10 bg-slate-900 text-white font-mono text-sm
                     hover:bg-slate-700 transition-colors flex items-center justify-center
                     shadow-lg hover:shadow-xl"
        >
          ←
        </motion.button>
        <motion.button
          aria-label="Next"
          onClick={next}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="pointer-events-auto absolute -right-4 top-1/2 -translate-y-1/2 z-20
                     w-10 h-10 bg-slate-900 text-white font-mono text-sm
                     hover:bg-slate-700 transition-colors flex items-center justify-center
                     shadow-lg hover:shadow-xl"
        >
          →
        </motion.button>
      </div>

      <Nudge />
    </section>
  );
}

/* --------------------------------- UI -------------------------------- */
function Header() {
  return (
    <div className="flex items-end justify-between">
      <div>
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-100">Projects Preview</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">A few featured builds—swipe to browse.</p>
      </div>
      <a href="/projects" className="text-sm text-blue-600 dark:text-blue-400 underline hover:opacity-80">Explore all</a>
    </div>
  );
}

function Nudge() {
  return (
    <motion.div
      className="mt-6 text-center"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <p className="text-sm text-slate-600 dark:text-slate-400">
        See full write-ups, repos, and more on the main projects page.
      </p>
      <motion.a
        href="/projects"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-2 border-slate-900 dark:border-slate-600
                   hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(148,163,184,0.5)]
                   hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
      >
        Explore all projects <span aria-hidden>↗</span>
      </motion.a>
    </motion.div>
  );
}
